import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const emptyLine = () => ({ productId: '', expectedQty: '0', countedQty: '0' });

export function CycleCountModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [warehouseId, setWarehouseId] = useState('');
  const [countedAt, setCountedAt] = useState(todayIso());
  const [lines, setLines] = useState([emptyLine()]);
  const [error, setError] = useState(null);

  const warehouses = useQuery({ queryKey: ['inventory', 'warehouses'], queryFn: () => inventoryApi.listWarehouses().then((r) => r.items), enabled: open });
  const products = useQuery({ queryKey: ['inventory', 'products', 'all'], queryFn: () => inventoryApi.listProducts({ limit: 200 }).then((r) => r.items), enabled: open });

  useEffect(() => {
    if (open) {
      setWarehouseId('');
      setCountedAt(todayIso());
      setLines([emptyLine()]);
      setError(null);
    }
  }, [open]);

  const setLine = (idx, patch) => setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const removeLine = (idx) => setLines((ls) => (ls.length > 1 ? ls.filter((_, i) => i !== idx) : ls));

  // Prefill the expected (system) quantity from the product's on-hand in the
  // selected warehouse, so the counter only fills in what they physically found.
  async function pickProduct(idx, productId) {
    setLine(idx, { productId, expectedQty: '0', countedQty: '0' });
    if (!productId || !warehouseId) return;
    try {
      const levels = await inventoryApi.stockByProduct(productId).then((r) => r.items);
      const onHand = levels.find((l) => l.warehouseId === warehouseId)?.quantity ?? '0';
      setLine(idx, { productId, expectedQty: String(onHand), countedQty: String(onHand) });
    } catch {
      /* leave at 0 - the count still posts */
    }
  }

  const save = useMutation({
    mutationFn: (body) => inventoryApi.createCycleCount(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'cycle-counts'] });
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    const valid = lines.filter((l) => l.productId);
    if (!warehouseId || valid.length === 0) {
      setError(t('cyc.error.required'));
      return;
    }
    save.mutate({
      warehouseId,
      countedAt,
      lines: valid.map((l) => ({ productId: l.productId, expectedQty: Number(l.expectedQty || 0), countedQty: Number(l.countedQty || 0) })),
    });
  }

  const productOptions = products.data || [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('cyc.modal.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={save.isPending}>
            {save.isPending ? `${t('common.save')}…` : t('common.save')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="form-stack">
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('cyc.field.warehouse')}</span>
            <select className="input" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} required>
              <option value="">-</option>
              {(warehouses.data || []).map((w) => <option key={w.id} value={w.id}>{w.code} · {w.name}</option>)}
            </select>
          </label>
          <label className="field">
            <span className="field-label">{t('cyc.field.date')}</span>
            <input className="input" type="date" value={countedAt} onChange={(e) => setCountedAt(e.target.value)} />
          </label>
        </div>

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{t('cyc.field.product')}</th>
                <th className="num">{t('cyc.field.expected')}</th>
                <th className="num">{t('cyc.field.counted')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => (
                <tr key={idx}>
                  <td>
                    <select className="input" value={l.productId} onChange={(e) => pickProduct(idx, e.target.value)}>
                      <option value="">-</option>
                      {productOptions.map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>)}
                    </select>
                  </td>
                  <td className="num"><input className="input num" type="number" min="0" step="1" value={l.expectedQty} onChange={(e) => setLine(idx, { expectedQty: e.target.value })} /></td>
                  <td className="num"><input className="input num" type="number" min="0" step="1" value={l.countedQty} onChange={(e) => setLine(idx, { countedQty: e.target.value })} /></td>
                  <td><Button size="sm" variant="ghost" onClick={() => removeLine(idx)} type="button">×</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Button size="sm" variant="ghost" type="button" onClick={addLine}>{t('cyc.action.addLine')}</Button>

        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
